import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Smartphone, Users, BarChart3, QrCode, Shield, Zap, Check, X, Mail, Phone } from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';
import { PLAN_FEATURES, PLAN_LABELS, FEATURE_LABELS } from '../constants/subscriptions';
import type { SubscriptionPlan } from '../services/subscriptions';

const LandingPage: React.FC = () => {
  const { pricing, supportEmail, supportPhone, loading: pricingLoading } = useConfig();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const formatPrice = (plan: SubscriptionPlan, period: 'monthly' | 'yearly') => {
    if (plan === 'trial') {
      return { primary: 'Included for 15 days', secondary: 'Auto-activated on first login' };
    }
    if (plan === 'free') {
      return { primary: '₹0', secondary: 'Limited access mode' };
    }
    const amount = pricing?.[plan]?.[period];
    if (amount === undefined || amount === null) {
      return { primary: '—', secondary: '' };
    }
    return {
      primary: `₹${amount}/${period === 'monthly' ? 'month' : 'year'}`,
      secondary: period === 'monthly' && pricing?.[plan]?.yearly
        ? `or ₹${pricing[plan].yearly}/year`
        : period === 'yearly' && pricing?.[plan]?.monthly
          ? `or ₹${pricing[plan].monthly}/month`
          : '',
    };
  };

  const getPlanDescription = (plan: SubscriptionPlan) => {
    switch (plan) {
      case 'trial':
        return 'Full access to every feature for your first 15 days.';
      case 'free':
        return 'Stay visible online with basic profile and public page access.';
      case 'basic':
        return 'Unlock social and custom links to grow your presence.';
      case 'premium':
        return 'Everything in Basic plus payment integrations.';
      default:
        return '';
    }
  };

  const allFeatures = Object.keys(FEATURE_LABELS);
  const plans: SubscriptionPlan[] = ['trial', 'free', 'basic', 'premium'];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="MY1QR" className="h-10 w-auto" />
              <span className="text-2xl font-bold text-primary-600">MyOneQR</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <a
                href="#features"
                className="text-secondary-600 hover:text-primary-600 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Features
              </a>
              <a
                href="#pricing"
                className="text-secondary-600 hover:text-primary-600 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Pricing
              </a>
              <a
                href="#support"
                className="text-secondary-600 hover:text-primary-600 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#support')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Support
              </a>
            </nav>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-secondary-600 hover:text-primary-600 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="btn-primary text-white"
              >
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold text-secondary-900 mb-6">
                One Link.
                <span className="text-primary-600"> Endless Connections.</span>
              </h1>
              <p className="text-xl text-secondary-600 mb-8 max-w-2xl">
                Create your digital identity with a beautiful profile page that showcases your links,
                collaborations, and professional presence all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/signup"
                  className="btn-primary text-white text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center">
                  View Demo
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm mx-auto">
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white text-center">
                  <div className="w-20 h-20 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Smartphone className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-white">John Doe</h3>
                  <p className="text-primary-100 mb-4">Digital Creator & Entrepreneur</p>
                  <div className="space-y-3">
                    <div className="bg-white/20 rounded-lg p-3 flex items-center">
                      <div className="w-8 h-8 bg-white/30 rounded-full mr-3"></div>
                      <span className="text-sm">Instagram</span>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center">
                      <div className="w-8 h-8 bg-white/30 rounded-full mr-3"></div>
                      <span className="text-sm">LinkedIn</span>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center">
                      <div className="w-8 h-8 bg-white/30 rounded-full mr-3"></div>
                      <span className="text-sm">Portfolio</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Everything you need to build your digital presence
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              From personal branding to business profiles, MY1QR provides all the tools
              you need to create a professional online presence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Smartphone className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Mobile-First Design</h3>
              <p className="text-secondary-600">
                Your profile looks perfect on any device with our responsive design that adapts to all screen sizes.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <QrCode className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">QR Code Generation</h3>
              <p className="text-secondary-600">
                Generate QR codes for easy sharing and offline promotion of your profile.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Collaborations</h3>
              <p className="text-secondary-600">
                Showcase your partnerships and collaborations with custom branding and descriptions.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Analytics</h3>
              <p className="text-secondary-600">
                Track your profile views and link clicks to understand your audience better.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Secure & Private</h3>
              <p className="text-secondary-600">
                Your data is protected with enterprise-grade security and privacy controls.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Lightning Fast</h3>
              <p className="text-secondary-600">
                Optimized for speed with global CDN and modern web technologies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Choose the perfect plan for you
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Start with a free trial and upgrade when you're ready to unlock more features.
            </p>
          </div>

          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border border-secondary-200">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${billingPeriod === 'monthly'
                  ? 'bg-primary-600 text-white'
                  : 'text-secondary-600 hover:text-primary-600'
                  }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${billingPeriod === 'yearly'
                  ? 'bg-primary-600 text-white'
                  : 'text-secondary-600 hover:text-primary-600'
                  }`}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          {pricingLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-secondary-600">Loading pricing...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan) => {
                const planFeatures = PLAN_FEATURES[plan];
                const pricingInfo = formatPrice(plan, billingPeriod);
                const isTrial = plan === 'trial';
                const isPremium = plan === 'premium';
                const isFree = plan === 'free';

                return (
                  <div
                    key={plan}
                    className={`card relative transition-all duration-200 hover:shadow-lg flex flex-col ${isPremium ? 'border-2 border-primary-500' : ''
                      } ${isTrial ? 'border-2 border-primary-300' : ''}`}
                  >
                    {/* Badges */}
                    {isPremium && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    {isTrial && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Try Free
                        </span>
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                        {PLAN_LABELS[plan]} Plan
                      </h3>
                      <p className="text-sm text-secondary-600 mb-4">
                        {getPlanDescription(plan)}
                      </p>
                      <div className="mt-4">
                        <div className="text-3xl font-bold text-secondary-900">
                          {pricingInfo.primary}
                        </div>
                        {pricingInfo.secondary && (
                          <div className="text-sm text-secondary-500 mt-1">
                            {pricingInfo.secondary}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="space-y-3 mb-6">
                      {allFeatures.map((feature) => {
                        const isIncluded = planFeatures.includes(feature as any);
                        return (
                          <div
                            key={feature}
                            className="flex items-start space-x-2"
                          >
                            {isIncluded ? (
                              <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <X className="h-5 w-5 text-secondary-400 flex-shrink-0 mt-0.5" />
                            )}
                            <span
                              className={`text-sm ${isIncluded
                                ? 'text-secondary-700'
                                : 'text-secondary-400 line-through'
                                }`}
                            >
                              {FEATURE_LABELS[feature]}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* CTA Button */}
                    <div className="mt-auto pt-4">
                      {isTrial ? (
                        <Link
                          to="/signup"
                          className="btn-primary w-full text-center block py-3"
                        >
                          Start Free Trial
                        </Link>
                      ) : isFree ? (
                        <Link
                          to="/signup"
                          className="btn-primary w-full text-center block py-3"
                        >
                          Get Started Free
                        </Link>
                      ) : (
                        <Link
                          to={`/signup?plan=${plan}`}
                          className={`w-full text-center block py-3 rounded-lg font-medium transition-colors ${isPremium
                            ? 'btn-primary'
                            : 'bg-secondary-100 hover:bg-secondary-200 text-secondary-900'
                            }`}
                        >
                          Choose {PLAN_LABELS[plan]}
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Trusted by creators and businesses worldwide
            </h2>
            <p className="text-xl text-secondary-600">
              Join thousands of users who have built their digital presence with MY1QR
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">C</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Creator</h3>
              <p className="text-secondary-600 mb-4">
                "MY1QR helped me consolidate all my social media links into one beautiful profile.
                My engagement increased by 40%!"
              </p>
              <p className="text-sm text-secondary-500">- Sarah Chen, Content Creator</p>
            </div>

            <div className="card text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">B</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Business</h3>
              <p className="text-secondary-600 mb-4">
                "Perfect for our team's business cards and email signatures.
                Professional and easy to update."
              </p>
              <p className="text-sm text-secondary-500">- Mike Johnson, Startup Founder</p>
            </div>

            <div className="card text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">P</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Professional</h3>
              <p className="text-secondary-600 mb-4">
                "As a freelancer, MY1QR gives me a professional online presence
                that impresses clients and generates leads."
              </p>
              <p className="text-sm text-secondary-500">- Alex Rodriguez, Freelancer</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Start building your digital identity today.
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of users who have already created their perfect online presence.
          </p>
          <Link
            to="/signup"
            className="bg-white text-primary-600 hover:bg-primary-50 font-semibold py-4 px-8 rounded-lg text-lg transition-colors duration-200 inline-flex items-center"
          >
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* MY1QR Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="MY1QR" className="h-12 w-auto" />
                <span className="text-2xl font-bold text-white">MyOneQR</span>
              </div>
              <p className="text-secondary-300">
                The ultimate platform for building your digital presence and connecting with your audience.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Links</h4>
              <ul className="space-y-2 text-secondary-300">
                <li>
                  <a
                    href="#features"
                    className="hover:text-white transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    className="hover:text-white transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Contact</h4>
              <ul className="space-y-3 text-secondary-300">
                {supportEmail && (
                  <li className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-secondary-400" />
                    <a
                      href={`mailto:${supportEmail}`}
                      className="hover:text-white transition-colors"
                    >
                      {supportEmail}
                    </a>
                  </li>
                )}
                {supportPhone && (
                  <li className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-secondary-400" />
                    <a
                      href={`tel:${supportPhone.replace(/\s/g, '')}`}
                      className="hover:text-white transition-colors"
                    >
                      {supportPhone}
                    </a>
                  </li>
                )}
                {!supportEmail && !supportPhone && (
                  <li className="text-secondary-400">Contact information coming soon</li>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-secondary-700 mt-8 pt-8 text-center text-secondary-300">
            <p>&copy; 2026 MY1QR. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;


